import type { FC } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useShallow } from 'zustand/react/shallow';
import { Stack, Typography } from '@mui/material';
import type { CashList } from '@pflegenavi/frontend/api-nursing-home';

import { useTransferFormStore } from './TransferFormStoreProvider';
import { CashListDropdown } from '../../CashListDropdown';

interface AccountSelectorProps {
  disabled: boolean;
  bucket: 'from' | 'to';
  /**
   * Restricts the dropdown options to these cash lists and auto-switches the
   * bucket's selection if the current one is excluded. Required: pass the full
   * configuration list to deliberately show all.
   */
  cashLists: CashList[];
}

export const AccountSelector: FC<AccountSelectorProps> = ({
  bucket,
  disabled,
  cashLists,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [bucketState] = useTransferFormStore(
    useShallow((state) => [state[bucket]])
  );

  const { color, title } = useMemo(() => {
    return {
      color:
        bucket === 'from'
          ? theme.palette.error.main
          : theme.palette.success.main,
      title: `${
        bucket === 'from' ? t('common.withdrawal') : t('common.deposit')
      }:`,
    };
  }, [bucket, theme.palette.error.main, theme.palette.success.main, t]);

  // Keep the bucket's selection in sync with the filtered options.
  useEffect(() => {
    if (cashLists === undefined || cashLists.length === 0) {
      return;
    }
    if (!cashLists.some((cl) => cl.id === bucketState.cashListId)) {
      bucketState.setCashListId(cashLists[0].id);
    }
  }, [cashLists, bucketState]);

  return (
    <Stack>
      <Typography fontWeight={700} variant="h6" color={color}>
        {title}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        maxWidth={350}
      >
        <Typography variant="subtitle2">
          {`${t('cashManagement.transfer.selected-cash-list')}:`}
        </Typography>
        <CashListDropdown
          label={t('cashManagement.select-cash-list')}
          disabled={disabled}
          size="small"
          selectedCashListId={bucketState.cashListId}
          setSelectedCashListId={bucketState.setCashListId}
          cashLists={cashLists}
        />
      </Stack>
    </Stack>
  );
};
